// Invoices.js - Added Send Email functionality
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../context/AuthContext";
import "./Invoices.scss";
import ReactDOM from "react-dom/client";
import {
  getInvoicesByID,
  getInvoicesCompany,
  getInvoicesCustomer,
  InvoicePaymentDetails,
  sendEmailByInvoiceID,
  updateInvoiceStatus,
} from "../../services/api/InvoicesApi";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import { InvoicePDFTemplate } from "../InvoicePDFTemplate/InvoicePDFTemplate";
import { useSearchResults } from "../../context/SearchResultsContext";
import { useLoading } from "../../context/LoadingContext";
import ConfirmationModal from "../../components/Modal/ConfirmModal";

import { useToast } from "../../context/ToastContext";

import { FaEye } from "react-icons/fa";
import { IoGrid } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";
import gsap from "gsap";
import { useInvoiceSelection } from "../../context/InvoiceSelectionContext";
import { formatText } from "../../components/TruncateParties";

export const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoicePayments, setSelectedInvoicePayments] = useState([]);
  const { role } = useAuth();
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState("");
  const { showLoading, hideLoading } = useLoading();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoiceTotal, setSelectedInvoiceTotal] = useState(0);

  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const {
    selectedCard: selectedInvoiceCard,
    handleCardClick: handleInvoiceCardClick,
    searchTerm: invoiceSearchTerm,
    setSearchTerm: setInvoiceSearchTerm,
    filters: invoiceFilters,
    setFilters: setInvoiceFilters,
    filteredInvoices,
    setFilteredInvoices,
    clearAllFilters: clearInvoiceFilters,
    hasActiveFilters: hasInvoiceFilters,
    refreshKey, // ADD THIS
    triggerInvoiceRefresh, // ADD THIS
    setIsLoading,
    isLoading,
    pagination,
    updatePaginationInfo,
    setPagination,
  } = useInvoiceSelection();

  const [viewMode, setViewMode] = useState("table");

  // GSAP refs for checkbox animations
  const checkboxRefs = useRef([]);

  const { searchResults, searchType } = useSearchResults();
  const { globalSettings } = useGlobalSettings();
  const { showToast } = useToast();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [cancelledRem, setCancelledRem] = useState("");

  // Map frontend filter keys to backend parameter names
  const filterKeyMap = {
    minAmount: 'minTotal',
    maxAmount: 'maxTotal'
  };

  const displayInvoices = useMemo(() => {
    return filteredInvoices || [];
  }, [filteredInvoices]);

  // Re-animate checkboxes when page changes or invoices list updates
  useEffect(() => {
    if (showCheckboxes && displayInvoices.length > 0) {
      // Use setTimeout to ensure DOM is updated before animating
      const timer = setTimeout(() => {
        animateCheckboxesIn();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [displayInvoices, showCheckboxes]);

  // For customer role, DetailSection is NOT mounted (Dashboard.js excludes it),
  // so we need to fetch invoices with backend filters directly here.
  useEffect(() => {
    if (role !== "customer") return; // Non-customer roles are handled by DetailSection

    const fetchInvoices = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();

        // Pagination
        if (pagination.limit !== "all") {
          params.set("page", pagination.page.toString());
          params.set("limit", pagination.limit.toString());
        }

        // Search term
        if (invoiceSearchTerm && invoiceSearchTerm.trim()) {
          params.set("search", invoiceSearchTerm.trim());
        }

        // Card-based filtering
        if (selectedInvoiceCard && selectedInvoiceCard !== "all") {
          params.set("onClick", selectedInvoiceCard);
        }

        // Filter parameters with backend key mapping
        if (invoiceFilters && Object.keys(invoiceFilters).length > 0) {
          Object.entries(invoiceFilters).forEach(([key, value]) => {
            if (
              value &&
              typeof value === "string" &&
              value.trim() !== "" &&
              value.trim() !== "all"
            ) {
              const backendKey = filterKeyMap[key] || key;
              params.set(backendKey, value.trim());
            }
          });
        }

        const queryString = params.toString();

        const response = await getInvoicesCustomer(
          queryString ? `?${queryString}` : ""
        );

        const invoiceData = response?.data || {};
        setFilteredInvoices(invoiceData.invoice || []);

        updatePaginationInfo(
          invoiceData.total || 0,
          pagination.limit === "all"
            ? false
            : (invoiceData.invoice || []).length === pagination.limit
        );
      } catch (err) {
        console.error("Error fetching invoices for customer:", err);
        setFilteredInvoices([]);
        updatePaginationInfo(0, false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [
    role,
    pagination.page,
    pagination.limit,
    selectedInvoiceCard,
    invoiceSearchTerm,
    invoiceFilters,
    refreshKey,
    setFilteredInvoices,
    updatePaginationInfo,
    setIsLoading,
  ]);


  const isInSearchMode = searchType === "invoices";

  const handleInvoicePerPageChange = useCallback(
    (e) => {
      const newLabelsPerPage =
        e.target.value === "all" ? "all" : Number(e.target.value);
      setPagination((prev) => ({
        ...prev,
        limit: newLabelsPerPage,
        page: 0,
      }));
    },
    [setPagination]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      setPagination((prev) => ({
        ...prev,
        page: newPage,
      }));
    },
    [setPagination]
  );

  const calculatePendingAmount = (invoice) => {
    if (!invoice.payments || invoice.payments.length === 0) {
      return invoice.totalAmount;
    }

    const totalPaid = invoice.payments.reduce(
      (sum, payment) => sum + (payment.amount || 0),
      0
    );
    return Math.max(0, invoice.totalAmount - totalPaid);
  };

  const handleViewReceipt = async (id) => {
    try {
      showLoading("Loading receipt details...");

      const response = await getInvoicesByID(id);
      const invoice = response.data;

      setSelectedInvoiceId(id);

      if (!invoice) {
        console.error("No invoice data found in response");
        showToast("No invoice data found. Please try again.", "error");
        return;
      }

      if (invoice?.cancelInvoiceRemark) {
        setCancelledRem(invoice.cancelInvoiceRemark);
      } else {
        setCancelledRem("");
      }

      let invoiceTotal = 0;

      if (invoice.totalAmount !== undefined && invoice.totalAmount !== null) {
        invoiceTotal = Number(invoice.totalAmount);
      } else if (invoice.total !== undefined && invoice.total !== null) {
        invoiceTotal = Number(invoice.total);
      } else if (invoice.amount !== undefined && invoice.amount !== null) {
        invoiceTotal = Number(invoice.amount);
      } else if (
        invoice.items &&
        Array.isArray(invoice.items) &&
        invoice.items.length > 0
      ) {
        invoiceTotal = invoice.items.reduce((sum, item) => {
          const itemAmount = Number(item.amount) || 0;
          return sum + itemAmount;
        }, 0);
      } else {
        invoiceTotal = 0;
      }

      const currentInvoiceStatus = invoice?.status || "pending";

      setSelectedInvoiceStatus(currentInvoiceStatus);
      setSelectedInvoiceTotal(invoiceTotal);
      setSelectedInvoicePayments(invoice?.payments || []);
      setStatusModalOpen(true);
    } catch (error) {
      console.error("Error fetching receipt details:", error);
      showToast("Failed to load receipt details. Please try again.", "error");
    } finally {
      hideLoading();
    }
  };

  const getNoDataMessage = () => {
    if (hasInvoiceFilters && displayInvoices.length === 0) {
      return "No invoices found matching your search and filters.";
    }

    if (searchType === "invoices" && displayInvoices.length === 0) {
      return "No invoices found matching your search.";
    }

    // Check role for default message
    return role === "customer"
      ? "No invoices found"
      : "No invoices found. Start by creating a new one!";
  };

  const shouldShowNoDataMessage = useMemo(() => {
    return displayInvoices.length === 0;
  }, [displayInvoices.length]);

  const handleSendEmail = async () => {
    if (!showCheckboxes) {
      setShowCheckboxes(true);
      return;
    }

    if (selectedInvoices.length === 0) {
      showToast("Please select at least one invoice", "error");
      return;
    }

    try {
      showLoading("Sending emails...");
      const payload = { invoiceIds: selectedInvoices };

      const response = await sendEmailByInvoiceID(payload);

      // Check if emailConfig exists and is false
      // if (response?.data?.emailConfig === false) {
      //   showToast(
      //     "Fill all fields for invoice email configuration in global settings",
      //     "error"
      //   );
      //   return response;
      // }

      // If emailConfig doesn't exist or is true, show success
      showToast(`Emails sent successfully for selected invoices!`, "success");

      // Animate checkboxes out before hiding
      animateCheckboxesOut();
      setTimeout(() => {
        setShowCheckboxes(false);
      }, 400); // Wait for animation to complete

      // Return the response if needed by the calling function
      return response;
    } catch (error) {
      console.error("Error sending emails:", error);
      showToast(`Failed to send emails: ${error.message}`, "error");
    } finally {
      hideLoading();
    }
  };
  const handleCheckboxChange = (id, event) => {
    if (event?.target) {
      animateCheckboxClick(event.target);
    }

    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((invId) => invId !== id) : [...prev, id]
    );
  };

  // GSAP Animation for showing checkboxes
  const animateCheckboxesIn = () => {
    const checkboxes = checkboxRefs.current.filter(
      (ref) => ref !== null && document.body.contains(ref)
    );

    if (checkboxes.length === 0) return;

    // Initial state - hide all checkboxes
    gsap.set(checkboxes, {
      opacity: 0,
      scale: 0.8,
      transformOrigin: "center center",
    });

    // Simple fade in with scale animation
    gsap.to(checkboxes, {
      duration: 0.4,
      opacity: 1,
      scale: 1,
      ease: "power2.out",
      stagger: {
        amount: 0.2,
        from: "start",
      },
    });
  };

  // GSAP Animation for hiding checkboxes
  const animateCheckboxesOut = () => {
    const checkboxes = checkboxRefs.current.filter(
      (ref) => ref !== null && document.body.contains(ref)
    );

    if (checkboxes.length === 0) return;

    const tl = gsap.timeline({
      onComplete: () => {
        setSelectedInvoices([]);
        checkboxRefs.current = [];
      },
    });

    // Simple fade out with scale animation
    tl.to(checkboxes, {
      duration: 0.3,
      opacity: 0,
      scale: 0.8,
      ease: "power2.in",
      stagger: {
        amount: 0.15,
        from: "end",
      },
    });
  };

  // Animation for individual checkbox interactions
  const animateCheckboxClick = (checkboxElement) => {
    // Simple scale animation on click
    gsap.to(checkboxElement, {
      duration: 0.15,
      scale: 0.9,
      ease: "power2.out",
      yoyo: true,
      repeat: 1,
    });
  };

  const handlePreview = async (id) => {
    try {
      showLoading("Loading...");

      const response = await getInvoicesByID(id);
      const invoice = response.data;
      const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings;

      const container = document.createElement("div");
      container.id = "pdf-preview-container";
      container.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 794px;
      min-height: auto;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: 9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      document.body.appendChild(container);

      // Create root and render
      const root = ReactDOM.createRoot(container);

      // Render the component
      root.render(
        <div
          style={{
            width: "794px",
            minHeight: "auto",
            backgroundColor: "white",
            display: "block",
            margin: 0,
            padding: 0,
          }}
        >
          <InvoicePDFTemplate invoice={invoice} settings={invoiceSettings} />
        </div>
      );

      // Wait for React to render and calculate actual height
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Wait for all images to load
      const allImages = container.querySelectorAll("img");

      if (allImages.length > 0) {
        await Promise.all(
          Array.from(allImages).map((img, index) => {
            return new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  console.warn(`Image ${index + 1} load timeout:`, img.src);
                  resolve();
                }, 8000);

                img.onload = () => {
                  clearTimeout(timeout);
                  resolve();
                };

                img.onerror = () => {
                  console.error(`Image ${index + 1} failed to load:`, img.src);
                  clearTimeout(timeout);
                  resolve();
                };
              }
            });
          })
        );
      }

      // Get the actual content height after rendering
      const actualHeight = Math.max(
        container.scrollHeight,
        container.offsetHeight,
        1123
      );

      // Update container with actual dimensions - position in viewport but behind everything
      container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 794px;
      height: ${actualHeight}px;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: -9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!container.innerHTML.trim()) {
        throw new Error("Container is empty - no content to convert to PDF");
      }

      const pdfWidth = 8.27;
      const pdfHeight = Math.max(11.69, actualHeight / 96);

      const options = {
        margin: 0,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: true,
          width: 794,
          height: actualHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
          windowHeight: actualHeight,
        },
        jsPDF: {
          unit: "in",
          format: [pdfWidth, pdfHeight],
          orientation: "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      const html2pdf = (await import("html2pdf.js")).default;

      // Generate PDF as blob instead of downloading
      const pdfBlob = await html2pdf()
        .set(options)
        .from(container)
        .outputPdf("blob");

      // Create blob URL and open in new tab
      const blobUrl = URL.createObjectURL(pdfBlob);
      const previewWindow = window.open(blobUrl, "_blank");

      if (!previewWindow) {
        showToast("Please allow popups for preview functionality", "error");
        URL.revokeObjectURL(blobUrl);
        return;
      }

      // Clean up the blob URL after some time
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 60000); // Clean up after 1 minute

      // Clean up DOM
      setTimeout(() => {
        try {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }, 1000);
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      showToast(`Failed to generate preview: ${error.message}`, "error");

      const container = document.getElementById("pdf-preview-container");
      if (container && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }
    } finally {
      hideLoading();
    }
  };

  const handleDownload = async (id) => {
    try {
      showLoading("Loading...");

      const response = await getInvoicesByID(id);
      const invoice = response.data;
      const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings;

      const container = document.createElement("div");
      container.id = "pdf-preview-container";
      container.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 794px;
      min-height: auto;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: 9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      document.body.appendChild(container);

      // Create root and render
      const root = ReactDOM.createRoot(container);

      // Render the component
      root.render(
        <div
          style={{
            width: "794px",
            minHeight: "auto",
            backgroundColor: "white",
            display: "block",
            margin: 0,
            padding: 0,
          }}
        >
          <InvoicePDFTemplate invoice={invoice} settings={invoiceSettings} />
        </div>
      );

      // Wait for React to render and calculate actual height
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Wait for all images to load
      const allImages = container.querySelectorAll("img");

      if (allImages.length > 0) {
        await Promise.all(
          Array.from(allImages).map((img, index) => {
            return new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  console.warn(`Image ${index + 1} load timeout:`, img.src);
                  resolve();
                }, 8000);

                img.onload = () => {
                  clearTimeout(timeout);
                  resolve();
                };

                img.onerror = () => {
                  console.error(`Image ${index + 1} failed to load:`, img.src);
                  clearTimeout(timeout);
                  resolve();
                };
              }
            });
          })
        );
      }

      // Get the actual content height after rendering
      const actualHeight = Math.max(
        container.scrollHeight,
        container.offsetHeight,
        1123
      );

      // Update container with actual dimensions - position in viewport but behind everything
      container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 794px;
      height: ${actualHeight}px;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: -9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!container.innerHTML.trim()) {
        throw new Error("Container is empty - no content to convert to PDF");
      }

      const pdfWidth = 8.27;
      const pdfHeight = Math.max(11.69, actualHeight / 96);

      const options = {
        margin: 0,
        filename: `${invoice.invoiceNumber || "invoice"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: true,
          width: 794,
          height: actualHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
          windowHeight: actualHeight,
        },
        jsPDF: {
          unit: "in",
          format: [pdfWidth, pdfHeight],
          orientation: "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      const html2pdf = (await import("html2pdf.js")).default;

      await html2pdf().set(options).from(container).save();

      // Clean up
      setTimeout(() => {
        try {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }, 1000);
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      showToast(`Failed to generate PDF: ${error.message}`, "error");

      const container = document.getElementById("pdf-preview-container");
      if (container && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }
    } finally {
      hideLoading();
    }
  };

  const handleStatusUpdateClick = async (id) => {
    try {
      showLoading("Loading invoice details...");

      // Always fetch fresh invoice data from API instead of using stale data from state
      const response = await getInvoicesByID(id);
      const invoice = response.data;

      setSelectedInvoiceId(id);

      // Check if invoice exists
      if (!invoice) {
        console.error("No invoice data found in response");
        showToast("No invoice data found. Please try again.", "error");
        return;
      }

      if (invoice?.cancelInvoiceRemark) {
        setCancelledRem(invoice.cancelInvoiceRemark);
      } else {
        setCancelledRem(""); // Clear any previous remark
      }
      if (invoice.items) {
        invoice.items.forEach((item, index) => {
          console.log(`Item ${index}:`);
        });
      }

      // For now, let's bypass the amount validation and see what happens
      let invoiceTotal = 0;

      // Try to find the total amount
      if (invoice.totalAmount !== undefined && invoice.totalAmount !== null) {
        invoiceTotal = Number(invoice.totalAmount);
      } else if (invoice.total !== undefined && invoice.total !== null) {
        invoiceTotal = Number(invoice.total);
      } else if (invoice.amount !== undefined && invoice.amount !== null) {
        invoiceTotal = Number(invoice.amount);
      } else if (
        invoice.items &&
        Array.isArray(invoice.items) &&
        invoice.items.length > 0
      ) {
        invoiceTotal = invoice.items.reduce((sum, item) => {
          const itemAmount = Number(item.amount) || 0;
          return sum + itemAmount;
        }, 0);
      } else {
        invoiceTotal = 0;
      }

      const currentInvoiceStatus = invoice?.status || "pending";

      // If status is cancelled, show modal with limited options
      if (currentInvoiceStatus === "cancelled") {
        setSelectedInvoiceStatus("cancelled");
        setSelectedInvoiceTotal(invoiceTotal);
        setSelectedInvoicePayments([]); // No payments for cancelled invoices
        setStatusModalOpen(true);
        return;
      }

      // For non-cancelled invoices, calculate status based on payments
      let calculatedStatus = currentInvoiceStatus;
      if (invoice?.payments && invoice.payments.length > 0) {
        const totalPaid = invoice.payments.reduce(
          (sum, p) => sum + (Number(p.amount) || 0),
          0
        );
        if (totalPaid >= invoiceTotal) {
          calculatedStatus = "paid";
        } else {
          calculatedStatus = "part-payment";
        }
      }

      setSelectedInvoiceStatus(calculatedStatus);
      setSelectedInvoiceTotal(invoiceTotal);
      setSelectedInvoicePayments(invoice?.payments || []);
      setStatusModalOpen(true);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      showToast("Failed to load invoice details. Please try again.", "error");
    } finally {
      hideLoading();
    }
  };

  const handleFilterChange = useCallback(
    (field, value) => {
      // Update context filters directly
      const newFilters = { ...invoiceFilters, [field]: value || "" };
      setInvoiceFilters(newFilters);
    },
    [invoiceFilters, setInvoiceFilters]
  );

  const getStatusOptions = () => {
    // If current status is cancelled, only allow viewing (no changes)
    if (selectedInvoiceStatus === "cancelled") {
      return [{ label: "Cancelled", value: "cancelled" }];
    }

    const baseOptions = [
      { label: "Pending", value: "pending" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Part-Payment", value: "part-payment" },
    ];

    // Only show "Paid" option if there are no existing payments
    if (!selectedInvoicePayments || selectedInvoicePayments.length === 0) {
      baseOptions.unshift({ label: "Paid", value: "paid" });
    }

    return baseOptions;
  };

  // REPLACE your existing handleConfirmStatusUpdate function:
  const handleConfirmStatusUpdate = async (
    newStatus,
    cancellationRemark = ""
  ) => {
    if (newStatus === "paid" || newStatus === "part-payment") {
      setStatusModalOpen(false);
      setSelectedInvoiceId(null);
      showToast("Payment details updated.", "success");

      // FIXED: Always refresh invoices after payment updates
      triggerInvoiceRefresh();
      return;
    }

    try {
      showLoading("Updating status...");

      // Handle cancellation with optional remark
      if (newStatus === "cancelled") {
        await updateInvoiceStatus(
          selectedInvoiceId,
          newStatus,
          cancellationRemark
        );
      } else {
        // For other status updates (no remark needed)
        await updateInvoiceStatus(selectedInvoiceId, newStatus);
      }

      setStatusModalOpen(false);
      setSelectedInvoiceId(null);
      showToast("Invoice status updated!", "success");

      // FIXED: Always refresh invoices after status updates
      triggerInvoiceRefresh();
    } catch (err) {
      console.error("Failed to update status:", err);
      showToast("Failed to update invoice status.", "error");
    } finally {
      hideLoading();
    }
  };



  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCancelStatusUpdate = () => {
    setStatusModalOpen(false);
    setSelectedInvoiceId(null);
  };

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOverflow, setDropdownOverflow] = useState("auto");

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      // Always show below the button, but limit maxHeight if needed
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      // Only close if scroll is outside the dropdown
      const dropdown = document.querySelector(".filter-dropdown-box");
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        // Scrolling inside the dropdown, do nothing
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  // Check if dropdown content is overflowing and set overflowY accordingly
  useEffect(() => {
    if (isFilterOpen && filterDropdownRef.current) {
      const el = filterDropdownRef.current;
      if (el.scrollHeight > el.clientHeight) {
        setDropdownOverflow("auto");
      } else {
        setDropdownOverflow("visible");
      }
    }
  }, [isFilterOpen, dropdownPosition]);

  // Cleanup GSAP animations on component unmount
  useEffect(() => {
    return () => {
      gsap.killTweensOf(checkboxRefs.current);
    };
  }, []);

  // Reset refs when invoices change
  useEffect(() => {
    checkboxRefs.current = [];
  }, [filteredInvoices]);

  useEffect(() => {
    // Expose function to clear local filters from external components
    window.clearInvoiceFilters = () => {
      clearInvoiceFilters();
    };

    return () => {
      delete window.clearInvoiceFilters;
    };
  }, [clearInvoiceFilters]);

  function formatNumberAbbr(num) {
    // Handle undefined, null, or invalid numbers
    if (num === undefined || num === null || num === "") return "0";
    if (typeof num !== "number") num = Number(num);
    if (isNaN(num)) return "0";

    // Round to 2 decimal places and remove trailing zeros
    return num.toFixed(2).replace(/\.00$/, '');
  }

  return (
    <>
      <div className={`invoices-container`}>
        {/* <h2>{role === "customer" ? "Your Invoices" : "Company Invoices"}</h2> */}
        <div className="invoices-header">
          <div className="view-toggle-wrapper">
            <button
              className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""
                }`}
              onClick={() => setViewMode("grid")}
            >
              <IoGrid />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === "table" ? "active" : ""
                }`}
              onClick={() => setViewMode("table")}
            >
              <FaTableList />
            </button>
            <div className="common-pagination-settings">
              <label htmlFor="invoicePerPageSelect">Invoices per page:</label>
              <select
                id="invoicePerPageSelect"
                value={pagination.limit}
                onChange={handleInvoicePerPageChange}
              >
                {[5, 10, 15, 20, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
                <option value="all">All</option>
              </select>
            </div>
          </div>

          <div className="header-buttons">
            {role !== "customer" && (
              <button className="send-email-btn" onClick={handleSendEmail}>
                {showCheckboxes ? "Send Selected" : "Send Email"}
              </button>
            )}
            {showCheckboxes && (
              <button
                className="cancel-select-btn"
                onClick={() => {
                  animateCheckboxesOut();
                  setTimeout(() => {
                    setShowCheckboxes(false);
                  }, 400); // Wait for animation to complete
                }}
              >
                Cancel
              </button>
            )}
            <div className="filter-dropdown-wrapper">
              <button
                className="filter-toggle-btn"
                onClick={() => setIsFilterOpen((prev) => !prev)}
                ref={filterButtonRef}
              >
                {isFilterOpen ? "Hide Filters" : "Show Filters"}
              </button>

              {isFilterOpen &&
                dropdownPosition.top !== 0 &&
                dropdownPosition.left !== 0 && (
                  <div
                    className="filter-dropdown-box"
                    ref={filterDropdownRef}
                    style={{
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      maxHeight: dropdownPosition.maxHeight
                        ? `${dropdownPosition.maxHeight}px`
                        : "500px",
                      overflowY: dropdownOverflow,
                      position: "fixed",
                    }}
                  >
                    <div className="filter-group">
                      <label>Status:</label>
                      <select
                        value={invoiceFilters.status}
                        onChange={(e) =>
                          handleFilterChange("status", e.target.value)
                        }
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="part-payment">Part-Payment</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    {role !== "customer" && (
                      <div className="filter-group">
                        <label>Client Name:</label>
                        <input
                          type="text"
                          placeholder="Enter client name"
                          value={invoiceFilters.clientName}
                          onChange={(e) =>
                            handleFilterChange("clientName", e.target.value)
                          }
                        />
                      </div>
                    )}

                    <div className="filter-group">
                      <label>Min Total:</label>
                      <input
                        type="number"
                        placeholder="₹ Min"
                        value={invoiceFilters.minAmount}
                        onChange={(e) =>
                          handleFilterChange("minAmount", e.target.value)
                        }
                        className="no-spinner"
                      />
                    </div>

                    <div className="filter-group no-spinner">
                      <label>Max Total:</label>
                      <input
                        type="number"
                        placeholder="₹ Max"
                        value={invoiceFilters.maxAmount}
                        onChange={(e) =>
                          handleFilterChange("maxAmount", e.target.value)
                        }
                        className="no-spinner"
                      />
                    </div>

                    <div className="filter-group">
                      <label>From Date:</label>
                      <input
                        type="date"
                        value={invoiceFilters.fromDate}
                        onChange={(e) =>
                          handleFilterChange("fromDate", e.target.value)
                        }
                      />
                    </div>

                    <div className="filter-group">
                      <label>To Date:</label>
                      <input
                        type="date"
                        value={invoiceFilters.toDate}
                        onChange={(e) =>
                          handleFilterChange("toDate", e.target.value)
                        }
                      />
                    </div>

                    <div className="filter-actions">
                      <button
                        className="clear-btn"
                        onClick={() => {
                          clearInvoiceFilters();
                        }}
                      >
                        Clear
                      </button>
                      <button
                        className="hide-btn"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Hide
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
        {(hasInvoiceFilters || Object.values(invoiceFilters).some((val) => val)) && (
          <div className="applied-filters">
            <span className="filters-text">Applied Filters:</span>

            {/* Search term filter pill */}
            {invoiceSearchTerm && invoiceSearchTerm.trim() && (
              <span
                className="filter-tag"
                onClick={() => {
                  setInvoiceSearchTerm("");
                  // Clear DetailSection's search input
                  if (window.clearDetailSectionSearch) {
                    window.clearDetailSectionSearch();
                  }
                }}
              >
                Search: {formatText(invoiceSearchTerm)} ×
              </span>
            )}

            {/* Card selection filter pill */}
            {selectedInvoiceCard && (
              <span
                className="filter-tag"
                onClick={() => handleInvoiceCardClick(selectedInvoiceCard)}
              >
                Card: {formatText(selectedInvoiceCard)} ×
              </span>
            )}

            {/* Individual local filter pills */}
            {invoiceFilters.status && (
              <span
                className="filter-tag"
                onClick={() => setInvoiceFilters({ ...invoiceFilters, status: "" })}
              >
                Status: {formatText(invoiceFilters.status)} ×
              </span>
            )}

            {invoiceFilters.clientName && (
              <span
                className="filter-tag"
                onClick={() => setInvoiceFilters({ ...invoiceFilters, clientName: "" })}
              >
                Client Name: {formatText(invoiceFilters.clientName)} ×
              </span>
            )}

            {invoiceFilters.minAmount && (
              <span
                className="filter-tag"
                onClick={() => setInvoiceFilters({ ...invoiceFilters, minAmount: "" })}
              >
                Min: ₹{formatText(invoiceFilters.minAmount)} ×
              </span>
            )}

            {invoiceFilters.maxAmount && (
              <span
                className="filter-tag"
                onClick={() => setInvoiceFilters({ ...invoiceFilters, maxAmount: "" })}
              >
                Max: ₹{formatText(invoiceFilters.maxAmount)} ×
              </span>
            )}

            {invoiceFilters.fromDate && (
              <span
                className="filter-tag"
                onClick={() => setInvoiceFilters({ ...invoiceFilters, fromDate: "" })}
              >
                Start Date: {formatText(invoiceFilters.fromDate)} ×
              </span>
            )}

            {invoiceFilters.toDate && (
              <span
                className="filter-tag"
                onClick={() => setInvoiceFilters({ ...invoiceFilters, toDate: "" })}
              >
                End Date: {formatText(invoiceFilters.toDate)} ×
              </span>
            )}

            <span
              className="filter-tag clear-all-tag"
              onClick={() => {
                clearInvoiceFilters();
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
                handleInvoiceCardClick(null);
              }}
            >
              Clear All
            </span>
          </div>
        )}

        {shouldShowNoDataMessage ? (
          <p className="no-invoice-found">{getNoDataMessage()}</p>
        ) : (
          <>
            {/* {(filters.status || filters.minAmount || filters.maxAmount || hasInvoiceFilters) && (
              <div className="applied-filters">
                <strong>Applied Filters:</strong>
                {selectedInvoiceCard && <span>Card: {selectedInvoiceCard}</span>}
                {invoiceSearchTerm && <span>Search: {invoiceSearchTerm}</span>}
                {filters.status && <span>Status: {filters.status}</span>}
                {filters.minAmount && <span>Min: ₹{filters.minAmount}</span>}
                {filters.maxAmount && <span>Max: ₹{filters.maxAmount}</span>}
                {filters.fromDate && <span>Start Date: {filters.fromDate}</span>}
                {filters.toDate && <span>End Date: {filters.toDate}</span>}
                {filters.clientName && <span>Client Name: {filters.clientName}</span>}
              </div>
            )} */}

            {viewMode === "grid" ? (
              <div className="invoice-grid">
                {displayInvoices.map((inv, index) => (
                  <div
                    className="invoice-card"
                    key={inv._id}
                    onClick={(e) => {
                      if (showCheckboxes) {
                        handleCheckboxChange(inv._id, e);
                      }
                    }}
                    style={{ cursor: showCheckboxes ? "pointer" : "default" }}
                  >
                    <div className="header">
                      <h4>{inv.invoiceNumber}</h4>
                      {showCheckboxes && (
                        <input
                          type="checkbox"
                          ref={(el) => (checkboxRefs.current[index] = el)}
                          className="checkbox-fade"
                          checked={selectedInvoices.includes(inv._id)}
                          onChange={(e) => handleCheckboxChange(inv._id, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <span className={`status ${inv.status}`}>
                        {inv.status}
                      </span>
                    </div>
                    {role !== "customer" && (
                      <div className="updating-status">
                        <button
                          type="button"
                          className="status-update-btn"
                          onClick={() => handleStatusUpdateClick(inv._id)}
                        >
                          Update Status
                        </button>
                      </div>
                    )}

                    {role === "customer" && (
                      <div className="updating-status">
                        <button
                          type="button"
                          className="view-receipt-btn"
                          onClick={() => handleViewReceipt(inv._id)}
                        >
                          View Receipt
                        </button>
                      </div>
                    )}
                    {/* <p>
                      <strong>Total:</strong> ₹{inv.totalAmount}
                    </p> */}
                    <div className="amount-dropdown">
                      <div className="amount-header">
                        <strong>Amount Details</strong>
                        <span className="dropdown-arrow">▼</span>
                      </div>
                      <div className="amount-details">
                        <div className="amount-row">
                          <span>Total:</span>
                          <span>₹{formatNumberAbbr(inv.totalAmount)}</span>
                        </div>
                        <div className="amount-row pending">
                          <span>Pending:</span>
                          <span>₹{formatNumberAbbr(calculatePendingAmount(inv))}</span>
                        </div>
                      </div>
                    </div>
                    <p>
                      <strong>Issued To:</strong> {inv.clientName}
                    </p>
                    {/* {inv.clientName && <p><strong>Client Name:</strong> {inv?.clientName}</p>} */}
                    <p>
                      <strong>Date:</strong>{" "}
                      {new Date(inv.createdAt).toLocaleDateString()}
                    </p>

                    <div className="items">
                      <strong>Items:</strong>
                      {inv.items.map((item, i) => (
                        <div className="item" key={i}>
                          <div>{item.description}</div>
                          <div>Qty: {item.qty}</div>
                          <div>₹{item.amount}</div>
                        </div>
                      ))}
                    </div>

                    {/* <div className="invoice-actions">
                      <button
                        type="button"
                        onClick={() => handlePreview(inv._id)}
                        disabled={
                          inv.status === "cancelled" ||
                          inv.status === "pending" ||
                          inv.status === "part-payment"
                        }
                        className={`preview-btn ${inv.status === "cancelled" ||
                            inv.status === "pending" ||
                            inv.status === "part-payment"
                            ? "disabled-btn"
                            : ""
                          }`}
                      >
                        <FaEye /> Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(inv._id)}
                        disabled={
                          inv.status === "cancelled" ||
                          inv.status === "pending" ||
                          inv.status === "part-payment"
                        }
                        className={`download-btn ${inv.status === "cancelled" ||
                            inv.status === "pending" ||
                            inv.status === "part-payment"
                            ? "disabled-btn"
                            : ""
                          }`}
                      >
                        Download
                      </button>
                    </div> */}

                    <div className="invoice-actions">
                      <button
                        type="button"
                        onClick={() => handlePreview(inv._id)}
                        className="preview-btn"
                      >
                        <FaEye /> Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(inv._id)}
                        className="download-btn"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <table className="invoice-table">
                <thead>
                  <tr>
                    {showCheckboxes && <th>Select</th>}
                    <th>Invoice No</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Items</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {displayInvoices.map((inv, index) => (
                    <tr
                      key={inv._id}
                      onClick={(e) => {
                        if (showCheckboxes) {
                          handleCheckboxChange(inv._id, e);
                        }
                      }}
                      style={{ cursor: showCheckboxes ? "pointer" : "default" }}
                    >
                      {showCheckboxes && (
                        <td>
                          <input
                            type="checkbox"
                            ref={(el) => (checkboxRefs.current[index] = el)}
                            className="checkbox-fade"
                            checked={selectedInvoices.includes(inv._id)}
                            onChange={(e) => handleCheckboxChange(inv._id, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      )}
                      <td>{inv.invoiceNumber}</td>
                      <td>
                        <span className={`status ${inv.status}`}>
                          {inv.status}
                        </span>
                      </td>
                      {/* <td>₹{inv.totalAmount}</td> */}
                      <td>
                        <div className="amount-dropdown table-dropdown">
                          <div className="amount-header">
                            <span>₹{formatNumberAbbr(inv.totalAmount)}</span>
                            <span className="dropdown-arrow">▼</span>
                          </div>
                          <div className="amount-details">
                            <div className="amount-row">
                              <span>Total:</span>
                              <span>₹{formatNumberAbbr(inv.totalAmount)}</span>
                            </div>
                            <div className="amount-row pending">
                              <span>Pending:</span>
                              <span>₹{formatNumberAbbr(calculatePendingAmount(inv))}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{inv.clientName}</td>
                      <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td>
                        {inv.items.map((item, idx) => (
                          <div key={idx}>
                            <ul className="item-list-square">
                              <li>
                                {" "}
                                {item.description} (Qty: {item.qty}, ₹
                                {item.amount})
                              </li>
                            </ul>
                          </div>
                        ))}
                      </td>
                      <td>
                        {role !== "customer" ? (
                          <button
                            onClick={() => handleStatusUpdateClick(inv._id)}
                          >
                            Update Status
                          </button>
                        ) : (
                          <button
                            onClick={() => handleViewReceipt(inv._id)}
                            className="view-receipt-btn"
                          >
                            View Receipt
                          </button>
                        )}
                        <button
                          onClick={() => handlePreview(inv._id)}
                          className="preview-btn"
                        >
                          <FaEye /> Preview
                        </button>
                        <button onClick={() => handleDownload(inv._id)}>
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        <ConfirmationModal
          isOpen={statusModalOpen}
          invoiceId={selectedInvoiceId}
          message={
            role === "customer" ? "Invoice Receipt" : "Update Invoice Status"
          }
          options={getStatusOptions()}
          currentStatus={selectedInvoiceStatus}
          totalAmount={selectedInvoiceTotal}
          previousPayments={selectedInvoicePayments}
          submitPaymentDetails={async (id, payments) => {
            const payload = { payments };
            await InvoicePaymentDetails(id, payload);
          }}
          onConfirm={handleConfirmStatusUpdate}
          onCancel={handleCancelStatusUpdate}
          activePage="Invoice"
          cancelledRem={cancelledRem}
          isViewOnly={role === "customer"} // Add this prop
        />
      </div>
      {pagination.limit !== "all" &&
        pagination.totalInvoices > 0 &&
        (pagination.totalInvoices > pagination.limit ||
          pagination.page > 0) && (
          <div className="pagination-controls">
            <button
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(0)}
            >
              First
            </button>
            <button
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span>
              Page {pagination.page + 1} of{" "}
              {Math.ceil(pagination.totalInvoices / pagination.limit)}
            </span>
            <button
              disabled={
                pagination.page >=
                Math.ceil(pagination.totalInvoices / pagination.limit) - 1
              }
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
            <button
              disabled={
                pagination.page >=
                Math.ceil(pagination.totalInvoices / pagination.limit) - 1
              }
              onClick={() =>
                handlePageChange(
                  Math.ceil(pagination.totalInvoices / pagination.limit) - 1
                )
              }
            >
              Last
            </button>
          </div>
        )}
    </>
  );
};
